import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getBillingProfile, createCheckout, createPortal, reconcileQuantity, createUpgradeCheckout } from '../api/billing';
import NavTabSelector from '../components/common/NavTabSelector';
import Button from '../components/common/Button';
import CloudBand from '../components/common/CloudBand';
import useFocusOnMount from '../hooks/useFocusOnMount';
import styles from './BillingPage.module.css';

export default function BillingPage() {
  const { t } = useTranslation();
  const mainRef = useFocusOnMount();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const ACCOUNT_TABS = [
    { key: 'account', label: t('billing.accountTab') },
    { key: 'billing', label: t('billing.subscriptionTab') },
    { key: 'install', label: t('billing.installTab') },
  ];

  const PLANS = [
    {
      id: 'annual',
      name: t('billing.annualPlan'),
      price: t('billing.annualPrice'),
      period: t('billing.annualPeriod'),
      subtitleLines: [t('billing.annualTagline1'), t('billing.annualTagline2')],
      saveBadge: t('billing.saveBadge'),
      popular: true,
      cta: t('billing.annualCta'),
    },
    {
      id: 'monthly',
      name: t('billing.monthlyPlan'),
      price: t('billing.monthlyPrice'),
      period: t('billing.monthlyPeriod'),
      subtitleLines: [t('billing.monthlyTagline')],
      saveBadge: null,
      popular: false,
      cta: t('billing.monthlyCta'),
    },
  ];

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const checkoutInFlight = useRef(false);

  const checkoutSuccess = searchParams.get('checkout') === 'success';

  useEffect(() => {
    setLoading(true);
    getBillingProfile()
      .then((data) => {
        setProfile(data);
        // Silent reconciliation — correct any Stripe/DB mismatch
        if (data?.has_subscription) {
          reconcileQuantity().catch(() => {}); // fire-and-forget
        }
      })
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, []);

  function handleTabChange(tab) {
    if (tab === 'account') navigate('/settings?tab=account');
    if (tab === 'install') navigate('/settings?tab=install');
    // 'billing' is this page — no-op
  }

  async function handleStartCheckout(planType) {
    // Ref guard — synchronous, prevents any double-fire before React re-renders
    if (checkoutInFlight.current) return;
    checkoutInFlight.current = true;
    setActionLoading(true);
    setError(null);
    try {
      const data = await createCheckout({ planType });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      setError(err.message || t('common.genericError'));
    } finally {
      setActionLoading(false);
      checkoutInFlight.current = false;
    }
  }

  async function handleManagePayment() {
    setActionLoading(true);
    setError(null);
    try {
      const data = await createPortal();
      if (data.portal_url) window.location.href = data.portal_url;
    } catch (err) {
      setError(err.message || t('billing.portalError'));
    } finally {
      setActionLoading(false);
    }
  }

  async function handleUpgradeToAnnual() {
    setActionLoading(true);
    setError(null);
    try {
      const data = await createUpgradeCheckout();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      setError(err.message || t('billing.upgradeError'));
    } finally {
      setActionLoading(false);
    }
  }

  function formatDate(ts) {
    if (!ts) return '';
    return new Date(ts).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric',
    });
  }

  const isTrial = !profile?.has_subscription;
  const isActive = profile?.subscription_status === 'active' || profile?.subscription_status === 'trialing';
  const isPastDue = profile?.subscription_status === 'past_due';
  const isCanceled = profile?.subscription_status === 'canceled';
  const isExpired = !profile?.has_access && !isTrial;
  const isAnnual = profile?.plan_type === 'annual';

  return (
    <main
      className={`${styles.page}${(!profile || isTrial) ? ` ${styles.trialPageBg}` : ''}`}
      ref={mainRef}
      tabIndex={-1}
    >
      <CloudBand variant="teal-800" className={styles.cloudBand} />

      {/* Qi — always visible, all subscription states */}
      <img
        src="/assets/mascot-qi-adventurous.webp"
        alt=""
        aria-hidden="true"
        className={styles.qiImage}
      />

      <div className={styles.content}>
        {/* ── Page Header — Back + Title + Tabs in one consistent row ── */}
        <div className={styles.pageHeader}>
          <div className={styles.pageHeaderLeft}>
            <Button variant="back" onClick={() => navigate('/')}>{t('common.back')}</Button>
            <h1 className={styles.pageTitle}>{t('billing.myAccount')}</h1>
          </div>
          <div className={styles.tabWrap}>
            <NavTabSelector tabs={ACCOUNT_TABS} active="billing" onChange={handleTabChange} />
          </div>
        </div>

        {checkoutSuccess && (
          <div className={styles.successBanner} role="status">
            <p className={styles.successText}>
              {searchParams.get('upgrade') === 'true'
                ? t('billing.upgradedToAnnual')
                : t('billing.allSet')}
            </p>
          </div>
        )}

        {error && (
          <div className={styles.errorBanner} role="alert">
            <p className={styles.errorText}>{error}</p>
          </div>
        )}

        {loading && <p className={styles.loadingText} aria-live="polite">{t('common.loading')}</p>}

        {!loading && profile && (
          <div className={styles.billingView}>

            {/* ── State 1: Trial (no subscription yet) ── */}
            {isTrial && profile.has_access && (
              <div className={styles.trialView}>

                {/* Single content column — all trial content lives here */}
                <div className={styles.trialContentCol}>

                  {/* Hero: heading + subtext */}
                  <div className={styles.heroSection}>
                    <h2 className={styles.heroHeadline}>
                      {profile.active_learner_name
                        ? t('billing.crewReady', { name: profile.active_learner_name })
                        : t('billing.crewReadyGeneric')}
                    </h2>
                    <p className={styles.heroSubtext}>
                      {profile.active_learner_name && profile.stories_read > 0
                        ? t('billing.statsMessage', {
                            name: profile.active_learner_name,
                            stories: profile.stories_read,
                            storyWord: profile.stories_read === 1 ? t('billing.story') : t('billing.stories'),
                            characters: profile.characters_met,
                            charWord: profile.characters_met === 1 ? t('billing.character') : t('billing.characters'),
                          })
                        : t('billing.adventuresWaiting')}
                    </p>
                  </div>

                  {/* Trial info card */}
                  <div className={styles.trialInfoCard}>
                    <p className={styles.trialInfoDays}>
                      {profile.trial_days_remaining > 0
                        ? t('billing.trialEndsIn', { count: profile.trial_days_remaining })
                        : t('billing.trialEndsToday')}
                    </p>
                    <p className={styles.trialInfoSub}>
                      {profile.trial_days_remaining > 0
                        ? t('billing.storiesLeft', { count: profile.trial_days_remaining })
                        : t('billing.lastChance')}
                    </p>
                  </div>

                  {/* Plan cards */}
                  <div className={styles.planCardsRow}>
                    {PLANS.map((plan) => (
                      <div key={plan.id} className={`${styles.planCard} ${plan.popular ? styles.planCardFeatured : styles.planCardSecondary}`}>
                        {plan.popular && (
                          <div className={styles.bestValueBadge}>
                            <span className={styles.bestValueBadgeText}>{t('billing.bestValue')}</span>
                          </div>
                        )}
                        <div className={styles.planTopSection}>
                          <div className={styles.planNameRow}>
                            <p className={styles.planName}>{plan.name}</p>
                            {plan.saveBadge && (
                              <div className={styles.saveBadge}>
                                <span className={styles.saveBadgeText}>{plan.saveBadge}</span>
                              </div>
                            )}
                          </div>
                          <div className={styles.priceRow}>
                            <span className={styles.priceAmount}>{plan.price}</span>
                            <span className={styles.pricePeriod}>{plan.period}</span>
                          </div>
                          <div className={styles.planSubtitleBlock}>
                            {plan.subtitleLines.map((line, i) => (
                              <p key={i} className={styles.planSubtitle}>{line}</p>
                            ))}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleStartCheckout(plan.id)}
                          disabled={actionLoading}
                          className={plan.popular ? styles.planBtnPrimary : styles.planBtnSecondary}
                        >
                          {actionLoading ? t('billing.pleaseWait') : plan.cta}
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Fine print */}
                  <p className={styles.trialFinePrint}>{t('billing.finePrint')}</p>

                </div>

              </div>
            )}

            {/* ── State 2: Active subscription ── */}
            {isActive && !isTrial && (
              <div className={styles.activeView}>
                <div className={styles.currentPlanCard}>
                  <p className={styles.planLabel}>{t('billing.currentPlan')}</p>
                  <p className={styles.planNameLarge}>
                    {isAnnual ? t('billing.annualPlan') : t('billing.monthlyPlan')}
                    {profile.subscription_status === 'trialing' && (
                      <span className={styles.trialTag}>{t('billing.trial')}</span>
                    )}
                  </p>
                  {profile.learner_count > 0 && (
                    <p className={styles.learnerCount}>
                      {t('billing.learnerCount', { count: profile.learner_count })}
                    </p>
                  )}
                  {profile.current_period_end && (
                    <p className={styles.renewalDate}>
                      {profile.cancel_at_period_end
                        ? t('billing.accessEnds', { date: formatDate(profile.current_period_end) })
                        : t('billing.renews', { date: formatDate(profile.current_period_end) })}
                    </p>
                  )}
                  <div className={styles.planActions}>
                    <button type="button" onClick={handleManagePayment} disabled={actionLoading} className={styles.btnManage}>
                      {t('billing.managePayment')}
                    </button>
                    <button type="button" onClick={handleManagePayment} disabled={actionLoading} className={styles.btnCancel}>
                      {t('billing.cancelSubscription')}
                    </button>
                  </div>
                </div>

                {/* Monthly → Annual upgrade CTA */}
                {!isAnnual && !profile.cancel_at_period_end && (
                  <div className={styles.upgradeCard}>
                    <div className={styles.upgradeContent}>
                      <p className={styles.upgradeHeading}>{t('billing.switchToAnnual')}</p>
                      <p className={styles.upgradeSubtext}>{t('billing.switchToAnnualSubtext')}</p>
                    </div>
                    <button
                      type="button"
                      onClick={handleUpgradeToAnnual}
                      disabled={actionLoading}
                      className={styles.btnUpgrade}
                    >
                      {actionLoading ? t('billing.pleaseWait') : t('billing.upgradeToAnnual')}
                    </button>
                  </div>
                )}

                {/* Unlimited learners callout */}
                <div className={styles.featuresCard}>
                  <p className={styles.featuresTitle}>{t('billing.whatsIncluded')}</p>
                  {[t('billing.featureStories'), t('billing.featureWorksheets'), t('billing.featureTracking'), t('billing.featureThemes'), t('billing.featureLearners')].map((f) => (
                    <div key={f} className={styles.featureRow}>
                      <svg width="14" height="10" viewBox="0 0 14 10" fill="none" aria-hidden="true">
                        <path d="M1 5L5 9L13 1" stroke="#2ab8a8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span className={styles.featureLabel}>{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── State 3: Past due ── */}
            {isPastDue && (
              <div className={styles.pastDueView}>
                <div className={styles.warningBanner}>
                  <p className={styles.warningTitle}>{t('billing.paymentFailed')}</p>
                  <p className={styles.warningSubtext}>
                    {t('billing.updatePaymentSubtext')}
                  </p>
                </div>
                <button type="button" onClick={handleManagePayment} disabled={actionLoading} className={styles.btnUpdatePayment}>
                  {t('billing.updatePaymentMethod')}
                </button>
              </div>
            )}

            {/* ── State 4: Canceled (still has access) ── */}
            {isCanceled && profile.has_access && (
              <div className={styles.canceledView}>
                <div className={styles.canceledCard}>
                  <p className={styles.canceledTitle}>{t('billing.subscriptionCanceled')}</p>
                  <p className={styles.canceledSubtext}>
                    {t('billing.accessUntil', { date: formatDate(profile.current_period_end) })}
                  </p>
                </div>
                <button type="button" onClick={() => handleStartCheckout('annual')} disabled={actionLoading} className={styles.btnReactivate}>
                  {t('billing.reactivateSubscription')}
                </button>
              </div>
            )}

            {/* ── State 5: Expired (no access) ── */}
            {(isExpired || (isTrial && !profile.has_access)) && (
              <div className={styles.expiredView}>
                <div className={styles.expiredCard}>
                  <p className={styles.expiredTitle}>
                    {isTrial ? t('billing.freeTrialEnded') : t('billing.subscriptionEnded')}
                  </p>
                  <p className={styles.expiredSubtext}>
                    {t('billing.choosePlan')}
                  </p>
                </div>
                {PLANS.map((plan) => (
                  <div key={plan.id} className={`${styles.planCard} ${plan.popular ? styles.planCardFeatured : styles.planCardSecondary}`}>
                    {plan.popular && (
                      <div className={styles.bestValueBadge}>
                        <span className={styles.bestValueBadgeText}>{t('billing.bestValue')}</span>
                      </div>
                    )}
                    <div className={styles.planTopSection}>
                      <div className={styles.planNameRow}>
                        <p className={styles.planName}>{plan.name}</p>
                        {plan.saveBadge && (
                          <div className={styles.saveBadge}>
                            <span className={styles.saveBadgeText}>{plan.saveBadge}</span>
                          </div>
                        )}
                      </div>
                      <div className={styles.priceRow}>
                        <span className={styles.priceAmount}>{plan.price}</span>
                        <span className={styles.pricePeriod}>{plan.period}</span>
                      </div>
                      <div className={styles.planSubtitleBlock}>
                        {plan.subtitleLines.map((line, i) => (
                          <p key={i} className={styles.planSubtitle}>{line}</p>
                        ))}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleStartCheckout(plan.id)}
                      disabled={actionLoading}
                      className={plan.popular ? styles.planBtnPrimary : styles.planBtnSecondary}
                    >
                      {actionLoading ? t('billing.pleaseWait') : plan.cta}
                    </button>
                  </div>
                ))}
              </div>
            )}

          </div>
        )}

        {/* No profile state */}
        {!loading && !profile && (
          <p className={styles.loadingText}>{t('billing.unableToLoad')}</p>
        )}
      </div>
    </main>
  );
}
